import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { createInhaleSound, createHeartbeatSound, createExhaleSound } from '../assets/sounds/breathing';
import SEO from '../components/SEO';

const Container = styled.main`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: var(--primary);
  margin-bottom: 2rem;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Balloon = styled(motion.div)<{ size: number }>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background: linear-gradient(135deg, #FF6B6B, #FF8E8E);
  border-radius: 50%;
  margin: 2rem auto;
  cursor: pointer;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 20px;
    height: 30px;
    background: #FF8E8E;
    border-radius: 0 0 10px 10px;
  }
`;

const Instruction = styled.h2`
  font-size: 1.5rem;
  color: var(--secondary);
  margin: 2rem 0;
`;

const Timer = styled.div`
  font-size: 3rem;
  font-weight: bold;
  color: var(--primary);
  margin: 1rem 0;
`;

const PhaseDescription = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-top: 1rem;
`;

const BreathingExercise: React.FC = () => {
  const [phase, setPhase] = useState<'idle' | 'inhale' | 'hold' | 'exhale'>('idle');
  const [timer, setTimer] = useState(0);
  const [balloonSize, setBalloonSize] = useState(150);
  const intervalsRef = useRef<number[]>([]);

  // Cleanup function for intervals
  const clearAllIntervals = () => {
    intervalsRef.current.forEach(clearInterval);
    intervalsRef.current = [];
  };

  useEffect(() => {
    return () => clearAllIntervals();
  }, []);

  const startExercise = () => {
    clearAllIntervals();
    setPhase('inhale');
    createInhaleSound();
    
    // Inhale phase - 4 seconds
    // Start from 150px and grow to 300px (double size)
    let size = 150;
    const growthPerStep = 150 / 40; // 40 steps over 4 seconds
    const inhaleInterval = setInterval(() => {
      size += growthPerStep;
      setBalloonSize(size);
    }, 100);
    intervalsRef.current.push(inhaleInterval);
    
    // After inhale phase
    setTimeout(() => {
      clearInterval(inhaleInterval);
      setPhase('hold');
      createHeartbeatSound();
      
      // Hold phase - 7 seconds
      let holdTime = 7;
      const holdInterval = setInterval(() => {
        holdTime--;
        setTimer(holdTime);
        if (holdTime === 0) {
          clearInterval(holdInterval);
          setPhase('exhale');
          createExhaleSound();
          
          // Exhale phase - 8 seconds
          let exhaleTime = 8;
          const startSize = 300;
          const endSize = 150;
          const totalSteps = 80; // 80 steps for smooth animation (10 steps per second)
          let currentStep = 0;
          
          const exhaleInterval = setInterval(() => {
            currentStep++;
            const progress = currentStep / totalSteps;
            const currentSize = startSize - (progress * (startSize - endSize));
            
            setBalloonSize(Math.max(endSize, currentSize));
            
            if (currentStep % 10 === 0) { // Update timer every second (10 steps)
              exhaleTime--;
              setTimer(exhaleTime);
            }
            
            if (currentStep >= totalSteps) {
              clearInterval(exhaleInterval);
              setPhase('idle');
              setBalloonSize(endSize);
              setTimer(0);
            }
          }, 100); // 100ms interval for smooth animation
          
          intervalsRef.current.push(exhaleInterval);
        }
      }, 1000);
      intervalsRef.current.push(holdInterval);
    }, 4000);
  };

  const getPhaseDescription = () => {
    switch (phase) {
      case 'inhale':
        return 'Breathe in deeply through your nose, filling your lungs completely';
      case 'hold':
        return 'Hold your breath, keeping your chest expanded';
      case 'exhale':
        return 'Exhale slowly through your mouth, making a whooshing sound';
      default:
        return 'Click the balloon to start the breathing exercise';
    }
  };

  return (
    <Container>
      <SEO 
        title="4-7-8 Breathing Exercise for Immediate Anxiety Relief"
        description="Experience instant stress relief with our guided 4-7-8 breathing technique. Interactive balloon animation and soothing sounds help you achieve immediate calm and relaxation. Perfect for anxiety, panic attacks, and stress management."
        keywords={[
          'breathing exercise',
          'anxiety relief',
          'stress relief',
          '4-7-8 breathing',
          'instant calm',
          'panic attack relief',
          'relaxation technique',
          'guided breathing',
          'stress management',
          'anxiety management',
          'breathing meditation',
          'calming exercise',
          'immediate stress relief',
          'anxiety breathing exercise',
          'stress reduction technique'
        ]}
        canonicalUrl="https://pop-fidget-toy.com/breathing"
      />
      
      <Title>4-7-8 Breathing Technique for Immediate Stress Relief</Title>
      
      <AnimatePresence mode="wait">
        <motion.div
          key={phase}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Balloon 
            size={balloonSize}
            onClick={phase === 'idle' ? startExercise : undefined}
            whileHover={phase === 'idle' ? { scale: 1.1 } : undefined}
            style={{ cursor: phase === 'idle' ? 'pointer' : 'default' }}
          />
          <Instruction>
            {phase === 'idle' ? 'Click the balloon to start' : 
             phase === 'inhale' ? 'Inhale...' :
             phase === 'hold' ? 'Hold...' : 'Exhale...'}
          </Instruction>
          {phase !== 'idle' && <Timer>{timer || ''}</Timer>}
          <PhaseDescription>{getPhaseDescription()}</PhaseDescription>
        </motion.div>
      </AnimatePresence>
    </Container>
  );
};

export default BreathingExercise; 